import { QueryParams } from 'common/urls/QueryParams';
import { chain, difference, every, find, first, isEmpty } from 'lodash';
import { useCallback, useMemo } from 'react';
import {
  BrowseFilter,
  HierarchicalAvailableCuisineCategoryFragment,
  SegmentFilter,
} from 'src/gqlReactTypings.generated.d';
import {
  useCuisineFiltersReducer,
  useFilterBarActions,
  useSubCuisineFiltersReducer,
} from 'src/pages/consumer/components/DishFirstHeader/store';
import { UseDishFirstExploreUrl } from 'src/pages/consumer/explore/useExploreUrl';
import { useTrackFilterChange } from '../hooks/useTrackFilterChange';
import {
  formatCuisines,
  formatSubcuisines,
  getRootCuisineForCuisine,
  getSubcuisinesForCuisine,
  isSubCuisine,
} from './utils';

export type CuisineFilterProps = {
  availableCuisineCategories: HierarchicalAvailableCuisineCategoryFragment[];
  allCuisineCategories: HierarchicalAvailableCuisineCategoryFragment[] | undefined;
  filters: UseDishFirstExploreUrl;
};

export const useCuisineFilters = ({
  allCuisineCategories,
  availableCuisineCategories,
  filters,
}: CuisineFilterProps) => {
  const { updateQueryParams, cuisineFilters } = filters;
  const { trackFilterChange } = useTrackFilterChange({ filters });
  const selectedCuisineId = first(cuisineFilters) ?? '';
  const selectedCuisine = find(allCuisineCategories, { id: selectedCuisineId });
  const rootCuisine = getRootCuisineForCuisine(selectedCuisine);
  const subCuisines = getSubcuisinesForCuisine(rootCuisine, availableCuisineCategories);

  const cuisineFiltersSection = useCuisineFiltersReducer();
  const subCuisineFiltersSection = useSubCuisineFiltersReducer();
  const filterBar = useFilterBarActions();

  const onChange = useCallback(
    (cuisineFilter: string[]) => {
      const updates = {
        [QueryParams.CUISINE_FILTER]: cuisineFilter,
        [QueryParams.BROWSE_FILTER]: BrowseFilter.Cuisine,
      };
      updateQueryParams(updates);
      trackFilterChange(updates);
    },
    [updateQueryParams, trackFilterChange]
  );

  const handleFilterChange = useCallback(
    (cuisineFilter: string) => {
      const isSelected = cuisineFilter === selectedCuisineId;
      onChange(isSelected || !cuisineFilter ? [] : [cuisineFilter]);
      cuisineFiltersSection.hide();
      subCuisineFiltersSection.hide();
    },
    [selectedCuisineId, cuisineFiltersSection, onChange, subCuisineFiltersSection]
  );

  const [moreCuisineCategories, isMoreSelected] = useMemo(() => {
    const filteredMoreCuisineCategories = chain(availableCuisineCategories)
      .filter({ isPrimary: false })
      .filter(({ ancestorCuisines }) => every(ancestorCuisines, { isPrimary: false }))
      .filter(({ subcuisines }) => every(subcuisines, { isPrimary: false }))
      .map('id')
      .value();

    return [
      filteredMoreCuisineCategories,
      !isEmpty(cuisineFilters) && isEmpty(difference(cuisineFilters, filteredMoreCuisineCategories)),
    ];
  }, [availableCuisineCategories, cuisineFilters]);

  return {
    allCuisinesButtonProps: {
      cuisineFilter: SegmentFilter.All,
      isSelected: isEmpty(cuisineFilters) || first(cuisineFilters) === SegmentFilter.All,
      title: 'All cuisines',
      onChange: () => handleFilterChange(''),
    },
    allSubCuisinesButtonProps: {
      cuisineFilter: rootCuisine?.id ?? SegmentFilter.All,
      isSelected: selectedCuisine?.isPrimary,
      title: rootCuisine ? `All ${rootCuisine.title} cuisines` : 'All subcuisines',
      onChange: useCallback(() => {
        onChange([rootCuisine?.id ?? '']);
        cuisineFiltersSection.hide();
        subCuisineFiltersSection.hide();
      }, [cuisineFiltersSection, onChange, rootCuisine?.id, subCuisineFiltersSection]),
    },
    moreCuisinesButtonProps: {
      cuisineFilter: SegmentFilter.More,
      isSelected: isMoreSelected,
      title: 'More',
      onChange: useCallback(() => {
        onChange(isMoreSelected ? [] : moreCuisineCategories);
        cuisineFiltersSection.hide();
        subCuisineFiltersSection.hide();
      }, [moreCuisineCategories, onChange, cuisineFiltersSection, subCuisineFiltersSection, isMoreSelected]),
    },
    formattedCuisines: formatCuisines(selectedCuisine, rootCuisine, availableCuisineCategories),
    formattedSubCuisines: formatSubcuisines(selectedCuisineId, rootCuisine, subCuisines),
    isSubCuisine: isSubCuisine(selectedCuisine, rootCuisine),
    showMoreIcon: !isEmpty(moreCuisineCategories),
    navigation: {
      cuisineFilters: cuisineFiltersSection,
      subCuisineFilters: subCuisineFiltersSection,
      filterBar,
    },
    handleFilterChange,
    rootCuisine,
    subCuisines,
    selectedCuisine,
    moreCuisineCategories,
  };
};
